/**
 * CalendarView — /calendars/:slug
 *
 * Month grid; pick a day to see/edit its detail in a side panel.
 * Owner can edit; non-owners can follow + tick days complete.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useSEO } from '../useSEO';
import { calendarsApi, shareUrl } from './api';
import DayDetail from './DayDetail';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(year, monthIdx) {
  // Mon-first weeks. Returns array of {date, inMonth} for 6 rows × 7 cols.
  const first = new Date(year, monthIdx, 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon=0, Sun=6
  const start = new Date(year, monthIdx, 1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ date: d, inMonth: d.getMonth() === monthIdx });
  }
  return cells;
}

function CalendarLoginGate({ slug }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const next = encodeURIComponent(`/calendars/${slug}`);

  useEffect(() => {
    let cancelled = false;
    calendarsApi.preview(slug)
      .then((r) => { if (!cancelled) setPreview(r.preview); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Calendar not found'); });
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="tt-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem 1rem' }}>
      <div className="cal-modal cal-modal-narrow" style={{ width: 'min(480px, 100%)' }}>
        <div className="cal-modal-head">
          <h3 style={{ margin: 0 }}>You’re invited 👋</h3>
        </div>
        <div style={{ padding: '1.1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {error ? (
            <p className="tt-status error" style={{ margin: 0 }}>{error}</p>
          ) : !preview ? (
            <p style={{ margin: 0, color: 'var(--muted)' }}>Loading…</p>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span aria-hidden style={{ fontSize: '2.2rem' }}>{preview.coverEmoji || '📅'}</span>
                <div>
                  <p style={{ margin: 0, fontSize: '1.05rem' }}>
                    <strong>{preview.ownerName}</strong> shared a calendar with you:
                  </p>
                  <p style={{ margin: '4px 0 0', fontWeight: 600 }}>{preview.title}</p>
                </div>
              </div>
              <p style={{ margin: 0, color: 'var(--muted)' }}>
                Log in or create a free account to view this calendar, follow it, tick off days as you complete them, and write directly on the PDFs to save your attempts.
              </p>
            </>
          )}
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
            <Link to={`/register?next=${next}`} className="primary-btn" style={{ textDecoration: 'none' }}>Create free account</Link>
            <Link to={`/login?next=${next}`} className="secondary-btn" style={{ textDecoration: 'none' }}>Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportModal({ slug, token, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  async function send(e) {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true); setError('');
    try {
      await calendarsApi.report(slug, reason.trim(), token);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to submit report');
    } finally { setBusy(false); }
  }
  return (
    <div className="cal-modal-backdrop" onClick={onClose} role="presentation">
      <div className="cal-modal cal-modal-narrow" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Report calendar">
        <div className="cal-modal-head">
          <h3>Report this calendar</h3>
          <button type="button" className="cal-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {done ? (
          <>
            <p>Thanks — our team has been notified.</p>
            <div className="cal-edit-actions"><button className="primary-btn" onClick={onClose}>Close</button></div>
          </>
        ) : (
          <form onSubmit={send} className="cal-upload-form">
            {error && <div className="tt-status error">{error}</div>}
            <label className="tt-field">
              <span>What’s wrong?</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={5}
                placeholder="Briefly describe the issue (e.g. inappropriate content, copyright, spam)."
                maxLength={2000}
              />
            </label>
            <div className="cal-edit-actions">
              <button type="button" className="secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={busy || !reason.trim()}>{busy ? 'Sending…' : 'Send report'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function CalendarView() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  if (authLoading) return null;
  if (!user) return <CalendarLoginGate slug={slug} />;
  return <CalendarViewAuthed slug={slug} />;
}

function CalendarViewAuthed({ slug }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [cal, setCal] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => { const t = new Date(); return { y: t.getFullYear(), m: t.getMonth() }; });
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD
  const [titleDraft, setTitleDraft] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useSEO({
    title: cal?.title || 'Revision calendar',
    description: cal?.description ? cal.description.slice(0, 160) : 'A shareable revision calendar — open the share link to view and follow.',
    canonicalPath: `/calendars/${slug}`,
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await calendarsApi.get(slug, token);
      setCal(r.calendar);
      setTitleDraft(r.calendar.title);
    } catch (err) {
      setError(err.message || 'Failed to load calendar');
      setCal(null);
    } finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  // Seed view month from earliest day if nothing selected yet.
  useEffect(() => {
    if (cal && cal.days.length && selectedDate === null) {
      const first = cal.days[0];
      const d = new Date(first.eventDate + 'T00:00:00');
      setView({ y: d.getFullYear(), m: d.getMonth() });
    }
  }, [cal, selectedDate]);

  const daysByDate = useMemo(() => {
    const map = new Map();
    if (cal) for (const d of cal.days) map.set(d.eventDate, d);
    return map;
  }, [cal]);

  const cells = useMemo(() => buildMonthGrid(view.y, view.m), [view]);
  const monthLabel = useMemo(() =>
    new Date(view.y, view.m, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  [view]);

  const todayStr = ymd(new Date());

  function moveMonth(delta) {
    setView((v) => {
      const nd = new Date(v.y, v.m + delta, 1);
      return { y: nd.getFullYear(), m: nd.getMonth() };
    });
  }

  async function selectDate(dateStr) {
    setSelectedDate(dateStr);
    if (cal?.isOwner && !daysByDate.has(dateStr)) {
      // auto-create empty day on owner click of empty cell
      try {
        const r = await calendarsApi.upsertDay(cal.id, { eventDate: dateStr, title: '', notes: '' }, token);
        setCal((prev) => prev ? { ...prev, days: [...prev.days, { ...r.day, items: [], completed: false }].sort((a, b) => a.eventDate.localeCompare(b.eventDate)) } : prev);
      } catch (err) {
        setError(err.message || 'Failed to add day');
      }
    }
  }

  // ── owner actions ──────────────────────────────────────────────────────
  async function saveTitle() {
    if (!titleDraft.trim() || !cal) return;
    try {
      await calendarsApi.update(cal.id, { title: titleDraft.trim() }, token);
      setCal({ ...cal, title: titleDraft.trim() });
      setEditingTitle(false);
    } catch (err) { setError(err.message); }
  }

  async function toggleVisibility() {
    if (!cal) return;
    const next = cal.visibility === 'public' ? 'unlisted' : 'public';
    try {
      await calendarsApi.update(cal.id, { visibility: next }, token);
      setCal({ ...cal, visibility: next });
    } catch (err) { setError(err.message); }
  }

  async function copyShare() {
    try {
      await navigator.clipboard.writeText(shareUrl(slug));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  }

  async function deleteCalendar() {
    if (!cal || !window.confirm('Delete this calendar permanently? Followers will lose access.')) return;
    try {
      await calendarsApi.remove(cal.id, token);
      navigate('/calendars');
    } catch (err) { setError(err.message); }
  }

  async function updateDay(dayId, patch) {
    await calendarsApi.updateDay(cal.id, dayId, patch, token);
    setCal((prev) => ({ ...prev, days: prev.days.map((d) => d.id === dayId ? { ...d, ...patch } : d) }));
  }

  async function deleteDay(dayId) {
    if (!window.confirm('Remove this day and all its items?')) return;
    await calendarsApi.deleteDay(cal.id, dayId, token);
    setCal((prev) => ({ ...prev, days: prev.days.filter((d) => d.id !== dayId) }));
    setSelectedDate(null);
  }

  // Add an item: either create via API (data) or insert a server-returned item directly (uploadedItem)
  async function addItem(dayId, data, uploadedItem) {
    if (uploadedItem) {
      setCal((prev) => ({
        ...prev,
        days: prev.days.map((d) => d.id === dayId ? { ...d, items: [...d.items, uploadedItem] } : d),
      }));
      return;
    }
    const r = await calendarsApi.addItem(cal.id, dayId, data, token);
    setCal((prev) => ({
      ...prev,
      days: prev.days.map((d) => d.id === dayId ? { ...d, items: [...d.items, r.item] } : d),
    }));
  }

  async function removeItem(dayId, item) {
    if (!window.confirm('Remove this item?')) return;
    await calendarsApi.deleteItem(cal.id, dayId, item.id, token);
    setCal((prev) => ({
      ...prev,
      days: prev.days.map((d) => d.id === dayId ? { ...d, items: d.items.filter((i) => i.id !== item.id) } : d),
    }));
  }

  // ── follower actions ─────────────────────────────────────────────────
  async function toggleFollow() {
    if (!user) { navigate('/login'); return; }
    if (cal.isFollowing) {
      await calendarsApi.unfollow(slug, token);
      setCal({ ...cal, isFollowing: false, followerCount: Math.max(0, (cal.followerCount || 0) - 1) });
    } else {
      await calendarsApi.follow(slug, token);
      setCal({ ...cal, isFollowing: true, followerCount: (cal.followerCount || 0) + 1 });
    }
  }

  async function toggleDayDone(dayId, completed) {
    if (!user) { navigate('/login'); return; }
    await calendarsApi.toggleDayDone(slug, dayId, completed, token);
    setCal((prev) => ({
      ...prev,
      days: prev.days.map((d) => d.id === dayId ? { ...d, completed } : d),
    }));
  }

  if (loading) return <div className="tt-page"><div className="tt-status saving">Loading calendar…</div></div>;
  if (!cal) return <div className="tt-page"><div className="tt-status error">{error || 'Calendar not found'}</div></div>;

  const selectedDay = selectedDate ? daysByDate.get(selectedDate) : null;
  const canTick = !!user && !cal.isOwner;

  return (
    <div className="cal-view-page">
      <header className="cal-view-header">
        <div className="cal-view-title-block">
          <span className="cal-view-emoji" aria-hidden>{cal.coverEmoji || '📅'}</span>
          {cal.isOwner && editingTitle ? (
            <input
              className="cal-view-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setTitleDraft(cal.title); setEditingTitle(false); } }}
              maxLength={200}
              autoFocus
            />
          ) : (
            <h1
              className={`cal-view-title${cal.isOwner ? ' editable' : ''}`}
              onClick={cal.isOwner ? () => setEditingTitle(true) : undefined}
              title={cal.isOwner ? 'Click to rename' : undefined}
            >{cal.title}</h1>
          )}
          <span className={`cal-tag cal-tag-${cal.visibility}`}>
            {cal.visibility === 'unlisted' ? 'Link only' : 'Public'}
          </span>
        </div>
        <div className="cal-view-actions">
          <button type="button" className="secondary-btn" onClick={copyShare}>
            {copied ? '✓ Copied' : '🔗 Share'}
          </button>
          {cal.isOwner ? (
            <>
              <button type="button" className="secondary-btn" onClick={toggleVisibility}>
                Make {cal.visibility === 'public' ? 'link only' : 'public'}
              </button>
              <button type="button" className="cal-link-btn cal-link-danger" onClick={deleteCalendar}>Delete</button>
            </>
          ) : (
            <>
              <button type="button" className={`primary-btn${cal.isFollowing ? ' cal-following' : ''}`} onClick={toggleFollow}>
                {cal.isFollowing ? '✓ Following' : '+ Follow'}
              </button>
              <button type="button" className="cal-link-btn" onClick={() => setReportOpen(true)}>Report</button>
            </>
          )}
        </div>
      </header>

      {cal.description && <p className="cal-view-desc">{cal.description}</p>}
      {cal.ownerName && (
        <p className="cal-view-owner">
          by {cal.ownerName} · {cal.followerCount || 0} follower{cal.followerCount === 1 ? '' : 's'} · {cal.days.length} day{cal.days.length === 1 ? '' : 's'} planned
        </p>
      )}
      {error && <div className="tt-status error">{error}</div>}

      <div className="cal-grid-layout">
        <section className="cal-month">
          <div className="cal-month-toolbar">
            <button type="button" className="cal-icon-btn" onClick={() => moveMonth(-1)} aria-label="Previous month">‹</button>
            <h2 className="cal-month-label">{monthLabel}</h2>
            <button type="button" className="cal-icon-btn" onClick={() => moveMonth(1)} aria-label="Next month">›</button>
            <button type="button" className="secondary-btn cal-today-btn" onClick={() => { const t = new Date(); setView({ y: t.getFullYear(), m: t.getMonth() }); setSelectedDate(ymd(t)); }}>Today</button>
          </div>

          <div className="cal-grid" role="grid">
            {WEEKDAY_LABELS.map((w) => <div key={w} className="cal-grid-head">{w}</div>)}
            {cells.map(({ date, inMonth }) => {
              const ds = ymd(date);
              const day = daysByDate.get(ds);
              const classes = ['cal-cell'];
              if (!inMonth) classes.push('out');
              if (ds === todayStr) classes.push('today');
              if (selectedDate === ds) classes.push('selected');
              if (day) classes.push('has-items');
              if (day?.completed) classes.push('done');
              return (
                <button
                  key={ds}
                  type="button"
                  className={classes.join(' ')}
                  onClick={() => selectDate(ds)}
                  aria-label={ds}
                >
                  <span className="cal-cell-date">{date.getDate()}</span>
                  {day?.title && <span className="cal-cell-title">{day.title}</span>}
                  {day && day.items.length > 0 && (
                    <span className="cal-cell-stack">
                      {day.items.slice(0, 2).map((it) => (
                        <span key={it.id} className="cal-cell-pill">
                          {it.kind === 'paper' || it.kind === 'upload' ? '📄' : it.kind === 'link' ? '🔗' : '📝'}
                          <span>{it.label}</span>
                        </span>
                      ))}
                      {day.items.length > 2 && <span className="cal-cell-more">+{day.items.length - 2} more</span>}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section className="cal-side">
          {selectedDay ? (
            <DayDetail
              key={selectedDay.id}
              day={selectedDay}
              isOwner={cal.isOwner}
              isFollowing={cal.isFollowing}
              canTick={canTick}
              calendarId={cal.id}
              onUpdateDay={(patch) => updateDay(selectedDay.id, patch)}
              onDeleteDay={() => deleteDay(selectedDay.id)}
              onAddItem={(data, uploadedItem) => addItem(selectedDay.id, data, uploadedItem)}
              onRemoveItem={(item) => removeItem(selectedDay.id, item)}
              onToggleDone={(c) => toggleDayDone(selectedDay.id, c)}
            />
          ) : selectedDate ? (
            <div className="cal-side-empty">
              <p><strong>{new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</strong></p>
              <p className="muted">Nothing scheduled yet.</p>
              {cal.isOwner && <p className="muted">Click again or pick another day to start adding items.</p>}
            </div>
          ) : (
            <div className="cal-side-empty">
              <p className="muted">Pick a day to see what’s planned.</p>
              {!cal.isOwner && <p className="muted"><Link to="/calendars">← Back to calendars</Link></p>}
            </div>
          )}
        </section>
      </div>

      {reportOpen && <ReportModal slug={slug} token={token} onClose={() => setReportOpen(false)} />}
    </div>
  );
}
